import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useQueryClient } from '@tanstack/react-query';
import { DatalistInput } from '../components/dataListInput';
import { useSettings } from '../../../context/SettingContext';
import { PageLayoutWrapper } from '../../../components/FormPageLayoutWrapper';
import { SignatureModal } from '../../../components/Form/SignatureModel';
// import { AttachmentSection } from '../../../components/Form/AttachmentSection';
import { FormActionButtons } from '../../../components/Form/FormActionButton';
import type { GeoLevel } from '../../../types/database';
import type { User } from '../../../types/user';
// import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import type { LegalAdviceFormElevenData } from '../../../types/LegalAdvices/LegalAdviceFormEleven.type';
import { fetchLegalAdviceFormElevenById, saveLegalAdviceFormElevenDraft, submitLegalAdviceFormElevenForm } from '../../../services/LegalAdviceElevenService';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';

export const INITIAL_LEGAL_ADVICE_FORM_ELEVEN_DATA: LegalAdviceFormElevenData = {
  id: '',
  documentNumber: '',
  status: 'DRAFT',
  rejectionReason: '',
  submitDate: '',
  attachments: [],

  // Legal Advice Form Three specific fields
  office: '',
    // lawRegisterNo: '',
    courtName: '',
    executionCaseNo: '',
    decreeHolderName: '',
    judgmentDebtorName: '',
    representedParty: '',
    originalCaseDetails: '',
    higherCourtAppealStatus: 'မရှိ',
    higherCourtDecreeDetails: '',
    executionApplicationDate: '',
    courtObjectionStatus: 'မရှိ',
    objectionDetails: '',
    priorDecreeApplicationStatus: 'မရှိ',
    executionClosedDetails: '',
    executionOrderDetails: '',
    handlingOfficerOpinion: '',
    handlingOfficerName: '',
    handlingOfficerSubmitDate: '',
    handlingOfficerSignature: '',
    inChargeOfficerOpinion: '',
    inChargeOfficerName: '',
    inChargeOfficerSubmitDate: '',
    inChargeOfficerSignature: '',
};

interface LegalFormProps {
  id?: string;
  initialData?: Partial<LegalAdviceFormElevenData>;
}

export function LegalFormEleven({ id: propId, initialData }: LegalFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const id = propId || params.id;
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const [formData, setFormData] = useState<LegalAdviceFormElevenData>({
    ...INITIAL_LEGAL_ADVICE_FORM_ELEVEN_DATA,
    ...initialData,
  });
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean>(
    initialData?.status === 'APPROVED' || formData.status === 'APPROVED'
  );
   const [selectedGeoLevels, setSelectedGeoLevels] = useState<GeoLevel[]>([]);
  const [activeSigningRole, setActiveSigningRole] = useState<'incharge' | 'handling' | null>(null);

  const { settings } = useSettings();
  const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
  // const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as User | null

  useEffect(() => {
    if (formData.status === 'APPROVED') {
      setIsApproved(true);
    }
  }, [formData.status]);

  // Fetch form data by ID
  useEffect(() => {
    if (!id) return;

    let isSubscribed = true;
    setLoading(true);
    setError(null);

    fetchLegalAdviceFormElevenById(id)
      .then((data: LegalAdviceFormElevenData) => {
        if (isSubscribed) {
          setFormData((prev) => ({
            ...prev,
            ...data,
            id: data.id || prev.id || id || '',
          }));
          if (data.status === 'APPROVED') {
            setIsApproved(true);
          }
        }
      })
      .catch((err: any) => {
        if (isSubscribed) {
          setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [id]);

  // Form is editable ONLY if status is DRAFT/RETURNED AND it has NOT been approved yet
  const isEditable = (formData.status === 'DRAFT') && !isApproved;

  const isFormSigned = Boolean(
    formData.handlingOfficerSignature?.trim() && formData.handlingOfficerSignature?.trim() && 
    (formData.handlingOfficerName?.trim() || formData.inChargeOfficerName?.trim())
  );

  // Navigation Handler
  const handleBackNavigation = async () => {
    await queryClient.invalidateQueries({ queryKey: ['legalFormEleven'] });
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/legal-form-one');
    }
  };

  // // Form Field Event Handlers
  // const handleChange = (
  //   e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  // ) => {
  //   if (!isEditable) return;
  //   const { name, value } = e.target;
  //   setFormData((prev) => ({ ...prev, [name]: value }));
  // };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleClearDate = (name: keyof LegalAdviceFormElevenData) => {
      if (!isEditable) return;
      setFormData((prev) => ({ ...prev, [name]: '' }));
    };


  const renderDatePicker = (
    name: Extract<keyof LegalAdviceFormElevenData, string>
  ) => (
    <FormDatePicker
      name={name}
      value={(formData?.[name] as string) || ''}
      isEditable={isEditable}
      onChange={handleChange}
      onClear={handleClearDate}
    />
  );

  // const handleClearDate = (name: keyof LegalAdviceFormElevenData) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({ ...prev, [name]: '' }));
  // };

  const handleClearHandlingSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, handlingOfficerSignature: '' }));
  };

  const handleClearInChargeOfficerSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, inChargeOfficerSignature: '' }));
  };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!isEditable || !e.target.files) return;
  //   const filesArray = Array.from(e.target.files);
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: [...(prev.attachments || []), ...filesArray],
  //   }));
  // };

  // const handleRemoveFile = (index: number) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: (prev.attachments || []).filter((_, i) => i !== index),
  //   }));
  // };


  const handleOpenSignatureModal = (role: 'incharge' | 'handling') => {
    if (!isEditable) return;
    setActiveSigningRole(role);
    setIsSigning(true);
  };


  const handleSaveSignature = () => {
  if (sigCanvasRef.current) {
    // Replace getTrimmedCanvas() with getCanvas()
    const canvas = sigCanvasRef.current.getCanvas();
    const dataUrl = canvas.toDataURL('image/png');
    
    if (activeSigningRole === 'handling') {
      setFormData((prev) => ({ ...prev, handlingOfficerSignature: dataUrl }));
    } else {
      setFormData((prev) => ({ ...prev, inChargeOfficerSignature: dataUrl }));
    }
    
    setIsSigning(false);
    setActiveSigningRole(null);
  }
};

  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isEditable || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const payload: Partial<LegalAdviceFormElevenData> = { ...formData, status: 'DRAFT' };

      const response = await saveLegalAdviceFormElevenDraft(formData.id || id || null, payload);

      if (response && response.id) {
        setFormData((prev) => ({ ...prev, id: response.id, status: 'DRAFT' }));
      }

      alert('မူကြမ်း သိမ်းဆည်းပြီးပါပြီ (Draft Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      alert(err?.message || 'မူကြမ်း သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Approve & Save (အတည်ပြုသိမ်းဆည်းမည်)
  const handleApproveAndSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.handlingOfficerSubmitDate?.trim() || !formData.inChargeOfficerSubmitDate?.trim()) {
      alert('တင်ပြသည့်ရက်စွဲများ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    // const nameOfOfficer = (formData.officerName || '').trim();
    // if (!formData.officerSignatureImage?.trim() || !nameOfOfficer) {
    //   alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
    //   return;
    // }

    // const nameOfProsecutor = (formData.prosecutorName || '').trim();
    if (!isFormSigned) {
      alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      const payload: LegalAdviceFormElevenData = {
        ...formData,
        id: targetId || '',
        // officerName: nameOfOfficer,
        // prosecutorName: nameOfProsecutor,
        status: 'APPROVED',
      };

      const response = await saveLegalAdviceFormElevenDraft(targetId || null, payload);
      const newId = response?.id || targetId;

      setFormData((prev) => ({
        ...prev,
        id: newId,
        // officerName: nameOfOfficer,
        // prosecutorName: nameOfProsecutor,
        status: 'APPROVED',
      }));

      setIsApproved(true); // Locks form editing
      alert('အတည်ပြု သိမ်းဆည်းပြီးပါပြီ (Approved & Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to approve and save:', err);
      alert(err?.message || 'အတည်ပြု သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Submit To Superior (အထက်သို့ တင်ပြမည်)
  const handleSubmitToSuperior = async (e: React.MouseEvent,
    targetLevels?: GeoLevel[] | null
  ) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.handlingOfficerSubmitDate?.trim() || !formData.inChargeOfficerSubmitDate?.trim()) {
      alert('တင်ပြသည့်ရက်စွဲများ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

     // Use passed levels if available; otherwise use multi-select state
    const levelsToSubmit =
      targetLevels && targetLevels.length > 0 ? targetLevels : selectedGeoLevels;

    // Validate that at least one checkbox level is selected
    if (!levelsToSubmit || levelsToSubmit.length === 0) {
      alert('အထက်သို့ တင်ပြရန် အဆင့် (Level) အနည်းဆုံးတစ်ခု ရွေးချယ်ပေးပါရန်။');
      return;
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      if (!targetId) {
        const draftResponse = await saveLegalAdviceFormElevenDraft(null, { ...formData, status: 'DRAFT' });
        targetId = draftResponse?.id;
        if (!targetId) {
          throw new Error('အချက်အလက် သိမ်းဆည်းခြင်း အိုင်ဒီ (ID) မရရှိပါ');
        }

        setFormData((prev) => ({
          ...prev,
          ...draftResponse,
          id: targetId!,
        }));
      }

      // const nameOfProsecutor = (formData.prosecutorName || '').trim();
      // const nameOfOfficer = (formData.officerName || '').trim();
      // const hasSigned = Boolean(formData.officerSignatureImage?.trim() && nameOfOfficer && formData.prosecutorSignatureImage?.trim() && nameOfProsecutor);
      // const nextStatus = hasSigned ? 'APPROVED' : 'SUBMITTED';
  
      if(!isFormSigned){
        throw new Error('လက်မှတ်ထိုးပြီးမှသာ တင်ပြနိုင်ပါသည်');
      }
      const payload: LegalAdviceFormElevenData = {
        ...formData,
        id: targetId,
        // officerName: nameOfOfficer,
        // prosecutorName: nameOfProsecutor,
        status: 'SUBMITTED',
        submitDate: formData.handlingOfficerSubmitDate,
        targetGeoLevels: levelsToSubmit, // Array of selected levels
      };

      const response = await submitLegalAdviceFormElevenForm(targetId, payload);

      setFormData((prev) => ({
        ...prev,
        id: targetId,
        status: response?.status || 'SUBMITTED',
      }));

      setIsApproved(true); // Locks form editing after submission
      alert(
        response?.status ==='SUBMITTED'
          ?  'အထက်သို့ တင်ပြပြီးပါပြီ (Submitted Successfully)' : 'တင်ပြမှုမအောင်မြင်ပါ'
      );
      navigate('/legal-form-one');
    } catch (err: any) {
      console.error('Failed to submit form:', err);
      alert(err?.message || 'တင်ပြခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  const GEO_LEVEL_MAP: Record<string, string> = {
    NATIONAL: 'ရုံးချုပ်',
    STATE: 'ပြည်နယ်/တိုင်း',
    DISTRICT: 'ခရိုင်',
    TOWNSHIP: 'မြို့နယ်',
  };

  const renderStageProgression = () => {
  const status = formData.status || 'DRAFT';

  // Extract and format Geo Level text
  const rawGeo = formData.targetGeoLevels || formData.geoLevel;
  const formatGeo = (geo: any) => {
    if (typeof geo === 'string') return GEO_LEVEL_MAP[geo] || geo;
    if (Array.isArray(geo)) {
      return geo
        .map((g) => GEO_LEVEL_MAP[g] || g)
        .filter(Boolean)
        .join(' | ');
    }
    return '';
  };
  const geoText = formatGeo(rawGeo);

  const STATUS_CONFIG: Record<
    string,
    { label: string; bg: string; text: string; border: string; dot: string }
  > = {
    DRAFT: {
      label: 'မူကြမ်း',
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
    },
    APPROVED: {
      label: 'ကိုယ်တိုင်အတည်ပြုပြီး',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-600',
    },
    SUBMITTED: {
      label: 'တင်ပြပြီး',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
    },
    UNDER_REVIEW: {
      label: 'ကြည့်ရှုထားသည်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
    ATTACHED: {
      label: 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
  };

    const currentConfig = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;

      if (loading) {
        return (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
          </div>
        );
      }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 px-3.5 bg-slate-50 border border-slate-200 rounded-lg shadow-xs">
        {/* Left: Section Title & Location Subtext */}
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            လုပ်ငန်းစဉ် အခြေအနေ
          </span>
          {geoText && (
            <span className="text-xs text-slate-600 font-medium mt-0.5 flex items-center gap-1">
              <span className="text-slate-400">📍</span>
              <span>{geoText}</span>
            </span>
          )}
        </div>

        {/* Right: Modern Pill Badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentConfig.bg} ${currentConfig.text} ${currentConfig.border} self-start sm:self-center`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${currentConfig.dot}`} />
          <span>{currentConfig.label}</span>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 my-6 max-w-xl mx-auto">
        <p className="font-semibold">{error}</p>
        <button
          type="button"
          onClick={handleBackNavigation}
          className="mt-4 px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          နောက်သို့ ပြန်သွားမည်
        </button>
      </div>
    );
  }

  return (
      <PageLayoutWrapper
        handleBackNavigation={handleBackNavigation}
        renderStageProgression={renderStageProgression}
        isEditable={isEditable}
        isPreview={isPreview}
        readOnlyMessage={
          formData.status === 'APPROVED'
            ? 'စာရွက်စာတမ်းကို ကိုယ်တိုင်အတည်ပြုပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
            : formData.status === 'SUBMITTED'
              ? 'စာရွက်စာတမ်းကို အထက်သို့ တင်ပြပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
              : undefined
        }
        setIsPreview={setIsPreview}
      >
        {/* Main A4 Form Container */}
      <div className="w-full max-w-[850px] bg-white shadow-lg border border-gray-300 p-12 text-black space-y-12 text-xs md:text-sm leading-relaxed">
        
        {/* ခေါင်းစီးပိုင်း */}
        <div>
          <div className="text-right text-xs font-bold">ဥပရအမှုလိုက်ပုံစံ (၁၁)</div>
          
          <div className="text-center space-y-2 mt-2">
            <h1 className="text-base font-bold">ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်အစိုးရ</h1>
            <h2 className="text-sm font-bold">ပြည်ထောင်စုရှေ့နေချုပ်ရုံး</h2>
            
            <div className="flex justify-center items-center gap-2">
              <DatalistInput
                name="office"
                value={formData.office}
                onChange={(e) => setFormData({ ...formData, office: e.target.value })}
                placeholder=""
                options={OFFICE_OPTIONS}
              />
              <span className="font-bold">ဥပဒေရုံး</span>
            </div>

            {/* <div className="flex justify-center items-center pt-2">
              <span className="font-medium">ဥပဒေ မှတ်ပုံတင် (</span>
              <input type="text" name="lawRegisterNo" value={formData.lawRegisterNo} onChange={handleChange} className="w-16 bg-transparent border-b border-black text-center focus:outline-none font-bold" />
              <span className="font-medium">) စာမူတ်စဉ်</span>
            </div> */}

            <h3 className="text-sm font-bold pt-4 text-center">
              တရားမဇာရီမှု ပြီးပြတ်ချက်ပုံစံ
            </h3>
          </div>

          {/* အပိုင်း ၁။ ရုံးအချက်အလက်များ (၁ မှ ၁၃) */}
          <div className="mt-8 space-y-4">
            <div className="flex items-baseline">
              <span className="w-10 font-bold">၁။</span>
              <span className="w-64">တရားရုံးအမည်</span>
              <input type="text" name="courtName" value={formData.courtName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၂။</span>
              <span className="w-64">တရားမ ဇာရီမှု အမှတ်</span>
              <input type="text" name="executionCaseNo" value={formData.executionCaseNo} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၃။</span>
              <span className="w-64">တရားနိုင်အမည်</span>
              <input type="text" name="decreeHolderName" value={formData.decreeHolderName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၄။</span>
              <span className="w-64">တရားရှုံးအမည်</span>
              <input type="text" name="judgmentDebtorName" value={formData.judgmentDebtorName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၅။</span>
              <span className="w-64">မည်သူ့အတွက် လိုက်ပါဆောင်ရွက်သည်</span>
              <input type="text" name="representedParty" value={formData.representedParty} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2" />
            </div>

            <div className="flex flex-col space-y-1">
              <div className="flex items-baseline">
                <span className="w-10 font-bold">၆။</span>
                <span className="flex-1">တရားမ မူလမှု အမှတ်နှင့် စီရင်ချက်၊ အမိန့်၊ ဒီကရီချမှတ်သည့် ရက်စွဲ (စီရင်ချက်၊ အမိန့်နှင့် ဒီကရီပူးတွဲထားပါသည်)</span>
              </div>
              <textarea name="originalCaseDetails" value={formData.originalCaseDetails} onChange={handleChange} rows={3} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none mt-1" />
            </div>

            <div className="flex items-baseline gap-4">
              <div className="flex items-baseline">
                <span className="w-10 font-bold">၇။</span>
                <span className="w-80">အဆင့်မြင့်တရားရုံးသို့ အယူခံမှု၊ ပြင်ဆင်မှု၊ အထွေထွေလျှောက်ထားမှု ဝင်ရောက်ခဲ့ခြင်း ရှိ/မရှိ</span>
              </div>
              <select name="higherCourtAppealStatus" value={formData.higherCourtAppealStatus} onChange={handleChange} className="bg-transparent border-b border-black focus:outline-none font-bold py-0.5">
                <option value="ရှိ">ရှိ</option>
                <option value="မရှိ">မရှိ</option>
              </select>
            </div>

            <div className="flex flex-col space-y-1">
              <div className="flex items-baseline">
                <span className="w-10 font-bold">၈။</span>
                <span className="flex-1">အဆင့်မြင့် တရားရုံး၏ အယူခံမှု၊ ပြင်ဆင်မှု၏ စီရင်ချက်အမိန့် အကျဉ်းနှင့် ရက်စွဲ</span>
              </div>
              <textarea name="higherCourtDecreeDetails" value={formData.higherCourtDecreeDetails} onChange={handleChange} rows={3} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none mt-1" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၉။</span>
              <span className="w-64">ဇာရီလျှောက်လွှာတင်သွင်းသည့်ရက်စွဲ</span>
              <input type="text" name="executionApplicationDate" value={formData.executionApplicationDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2" />
            </div>

            <div className="flex items-baseline gap-4">
              <div className="flex items-baseline">
                <span className="w-10 font-bold">၁၀။</span>
                <span className="w-64">တရားရှုံးမှ ကန့်ကွက်ခြင်း ရှိ/မရှိ</span>
              </div>
              <select name="courtObjectionStatus" value={formData.courtObjectionStatus} onChange={handleChange} className="bg-transparent border-b border-black focus:outline-none font-bold py-0.5">
                <option value="ရှိ">ရှိ</option>
                <option value="မရှိ">မရှိ</option>
              </select>
            </div>

            <div className="flex flex-col space-y-1">
              <div className="flex items-baseline">
                <span className="w-10 font-bold">၁၁။</span>
                <span className="flex-1">ကန့်ကွက်လွှာတင်သည့် ရက်စွဲနှင့် အကြောင်းပြချက်အကျဉ်း</span>
              </div>
              <textarea name="objectionDetails" value={formData.objectionDetails} onChange={handleChange} rows={3} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none mt-1" />
            </div>

            <div className="flex items-baseline gap-4">
              <div className="flex items-baseline">
                <span className="w-10 font-bold">၁၂။</span>
                <span className="w-64">ပုဂ္ဂလိက ဒီကရီလျှောက်ထားမှု ရှိ/မရှိ (ရှိလျှင်ဖော်ပြရန်)</span>
              </div>
              <select name="priorDecreeApplicationStatus" value={formData.priorDecreeApplicationStatus} onChange={handleChange} className="bg-transparent border-b border-black focus:outline-none font-bold py-0.5">
                <option value="ရှိ">ရှိ</option>
                <option value="မရှိ">မရှိ</option>
              </select>
            </div>

            <div className="flex flex-col space-y-1">
              <div className="flex items-baseline">
                <span className="w-10 font-bold">၁၃။</span>
                <span className="flex-1">ဇာရီမှု ပိတ်သည့်ရက်စွဲနှင့် ပိတ်ရသည့် အကြောင်းပြချက်</span>
              </div>
              <textarea name="executionClosedDetails" value={formData.executionClosedDetails} onChange={handleChange} rows={3} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none mt-1" />
            </div>

            <div className="flex flex-col space-y-1">
              <div className="flex items-baseline">
                <span className="w-10 font-bold">၁၄။</span>
                <span className="flex-1">ဇာရီမှုတွင် ချမှတ်သည့် အမိန့်အကျဉ်းနှင့်ရက်စွဲ</span>
              </div>
              <textarea name="executionOrderDetails" value={formData.executionOrderDetails} onChange={handleChange} rows={3} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none mt-1" />
            </div>
          </div>
        </div>


        {/*အမှုလိုက် ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် (၁၅) */}
        <div className="space-y-4">
          <div className="flex flex-col space-y-1">
            <label className="font-bold"><span className="mr-4">၁၅။</span>အမှုလိုက် ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက်</label>
            <textarea name="handlingOfficerOpinion" value={formData.handlingOfficerOpinion} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none mt-1" />
          </div>

          <div className="flex flex-col items-end pt-2">
            <div className="w-64 space-y-2">
              <div className="flex items-center">
                <span className="w-24 text-right text-xs mr-2">လက်မှတ်</span>
                <div onClick={() => handleOpenSignatureModal('handling')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 relative">
                  {formData.handlingOfficerSignature ? (
                    <img src={formData.handlingOfficerSignature} alt="Handling Sign" className="h-8 object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                  )}
                  {formData.prosecutorSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearHandlingSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <span className="w-24 text-right text-xs mr-2">အမည်</span>
                <input type="text" name="handlingOfficerName" value={formData.handlingOfficerName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="text-center text-xs font-bold pt-1">အမှုလိုက်ဥပဒေအရာရှိ</div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('handlingOfficerSubmitDate')}</div>
                {/* <input type="text" name="handlingOfficerSubmitDate" value={formData.handlingOfficerSubmitDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
            </div>
          </div>
        </div>

        {/* တာဝန်ခံ ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် (၁၆) */}
        <div className="space-y-4">
          <div className="flex flex-col space-y-1">
            <label className="font-bold"><span className="mr-4">၁၆။</span>တာဝန်ခံ ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက်</label>
            <textarea name="inChargeOfficerOpinion" value={formData.inChargeOfficerOpinion} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none mt-1" />
          </div>

          <div className="flex flex-col items-end pt-2">
            <div className="w-64 space-y-2">
              <div className="flex items-center">
                <span className="w-24 text-right text-xs mr-2">လက်မှတ်</span>
                <div onClick={() => handleOpenSignatureModal('incharge')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 relative">
                  {formData.inChargeOfficerSignature ? (
                    <img src={formData.inChargeOfficerSignature} alt="In-charge Sign" className="h-8 object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                  )}
                  {formData.prosecutorSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearInChargeOfficerSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <span className="w-24 text-right text-xs mr-2">အမည်</span>
                <input type="text" name="inChargeOfficerName" value={formData.inChargeOfficerName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="text-center text-xs font-bold pt-1">တာဝန်ခံဥပဒေအရာရှိ</div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('inChargeOfficerSubmitDate')}</div>
                {/* <input type="text" name="inChargeOfficerSubmitDate" value={formData.inChargeOfficerSubmitDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
                  {/* Form Action Buttons Component Integration */}
                    <FormActionButtons
                      isPreview={isPreview}
                      isEditable={isEditable}
                      isSubmitting={isSubmitting}
                      isFormSigned={isFormSigned}
                      isApproved={isApproved}
                      targetGeoLevels={formData.targetGeoLevels}
                      currentGeoLevel={currentUser?.scopeLevel}
                      selectedGeoLevels={selectedGeoLevels}
                      onGeoLevelChange={setSelectedGeoLevels}
                      onCancel={handleBackNavigation}
                      onSaveDraft={handleSaveDraft}
                      onApproveAndSave={handleApproveAndSave}
                      onSubmitToSuperior={handleSubmitToSuperior}
                    />
          
                  {/* Attachment Section */}
                  {/* <AttachmentSection
                    attachments={formData.attachments}
                    isEditable={isEditable}
                    isPreview={isPreview}
                    onFileChange={handleFileChange}
                    onRemoveFile={handleRemoveFile}
                  /> */}
          
                  {/* Signature Modal */}
                  <SignatureModal
                    isOpen={isSigning}
                    sigCanvasRef={sigCanvasRef}
                    onClose={() => {
                      setIsSigning(false);
                      setActiveSigningRole(null);
                    }}
                    onClear={() => sigCanvasRef.current?.clear()}
                    onSave={handleSaveSignature}
                  />

      </div>
      </PageLayoutWrapper>
    );
  }